import { asDefined, ByteArrayInput, Option, UUID } from "@opendaw/lib-std";
import { PointerField } from "@opendaw/lib-box";
import { Pointers } from "@opendaw/studio-enums";
import { UnionBoxTypes } from "../unions";
export var TransferRegions;
(function (TransferRegions) {
    /**
     * Copies a region and its dependencies to a target track, works across BoxGraphs.
     * Preserved resources already present in the target graph are shared, not duplicated.
     * @returns the newly created region box in the target graph
     */
    TransferRegions.transfer = (region, targetTrack, insertPosition, deleteSource = true) => {
        const targetGraph = targetTrack.graph;
        const uniqueBoxes = UUID.newSet(box => box.address.uuid);
        uniqueBoxes.add(region);
        for (const dependency of region.graph.dependenciesOf(region, {
            alwaysFollowMandatory: true,
            stopAtResources: true,
            excludeBox: (dep) => dep.ephemeral
        }).boxes) {
            uniqueBoxes.add(dependency);
        }
        const skippedPreservedUuids = UUID.newSet(uuid => uuid);
        uniqueBoxes.forEach(box => {
            if (box.resource === "preserved" && targetGraph.findBox(box.address.uuid).nonEmpty()) {
                skippedPreservedUuids.add(box.address.uuid);
            }
        });
        const shouldSkipBox = (box) => {
            if (box.resource === "preserved" && skippedPreservedUuids.hasKey(box.address.uuid)) {
                return true;
            }
            for (const [, targetAddress] of box.outgoingEdges()) {
                if (skippedPreservedUuids.hasKey(targetAddress.uuid) && !targetAddress.isBox()) {
                    return true;
                }
            }
            return false;
        };
        const sourceBoxes = uniqueBoxes.values().filter(box => !shouldSkipBox(box));
        const uuidMap = UUID.newSet(entry => entry.source);
        sourceBoxes.forEach(box => {
            uuidMap.add({
                source: box.address.uuid,
                target: box.resource === "preserved" ? box.address.uuid : UUID.generate()
            });
        });
        skippedPreservedUuids.forEach(uuid => uuidMap.add({ source: uuid, target: uuid }));
        let result = null;
        PointerField.decodeWith({
            map: (pointer, address) => {
                const remapped = address.flatMap(addr => uuidMap.opt(addr.uuid).map(entry => addr.moveTo(entry.target)));
                if (remapped.nonEmpty()) {
                    return remapped;
                }
                if (pointer.pointerType === Pointers.RegionCollection) {
                    return Option.wrap(targetTrack.regions.address);
                }
                if (pointer.pointerType === Pointers.ClipCollection) {
                    return Option.wrap(targetTrack.clips.address);
                }
                return Option.None;
            }
        }, () => sourceBoxes.forEach(sourceBox => {
            const input = new ByteArrayInput(sourceBox.toArrayBuffer());
            const targetUuid = uuidMap.get(sourceBox.address.uuid).target;
            targetGraph.createBox(sourceBox.name, targetUuid, box => {
                box.read(input);
                if (UnionBoxTypes.isRegionBox(box)) {
                    const regionBox = UnionBoxTypes.asRegionBox(box);
                    regionBox.position.setValue(insertPosition);
                    result = regionBox;
                }
            });
        }));
        if (deleteSource) {
            region.delete();
        }
        return asDefined(result, "Failed to create region copy");
    };
})(TransferRegions || (TransferRegions = {}));
